import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Paper, Chip, Skeleton, Snackbar, Alert, TextField, FormControl, InputLabel, Select, MenuItem, Button, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Menu } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExportFormatModal from '../../../components/ExportFormatModal';
import { convertCsvToExportFormat } from '../../../utils/exportUtils';
import ReportService from '../../../api/services/reportService';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import ReplayIcon from '@mui/icons-material/Replay';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { formatCurrency } from '../../../utils/currency';
import { standardFilterInputSx, standardFilterSelectSx, standardResetButtonSx, standardSecondaryButtonSx } from '../../../components/common/tableStyles';

export default function OccupancyReportTab({ tenantId, roomTypes, isActive }) {
    const [occupancyData, setOccupancyData] = useState([]);
    const [occupancySummary, setOccupancySummary] = useState(null);
    const [isLoadingOccupancy, setIsLoadingOccupancy] = useState(false);
    const [occupancyError, setOccupancyError] = useState(null);
    
    const [occupancyFilters, setOccupancyFilters] = useState({
        startDate: '',
        endDate: '',
        period: 'daily',
        roomTypeId: ''
    });
    const [occupancyPage, setOccupancyPage] = useState(0);
    const [occupancyRowsPerPage, setOccupancyRowsPerPage] = useState(20);
    
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (isActive) {
            fetchOccupancyData();
        }
    }, [isActive, tenantId, occupancyFilters, occupancyPage, occupancyRowsPerPage]);

    const fetchOccupancyData = async (isExport = false) => {
        setIsLoadingOccupancy(true);
        setOccupancyError(null);
        try {
            const params = { ...occupancyFilters };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) {
                    delete params[key];
                }
            });
            
            if (isExport) {
                params.noPaging = true;
            } else {
                params.page = occupancyPage + 1;
                params.limit = occupancyRowsPerPage;
            }
            
            const res = await ReportService.getOccupancyReport(tenantId, params);
            const reportData = res?.data || res;
            setOccupancySummary(reportData);
            setOccupancyData(reportData?.trendData || reportData?.items || []);
        } catch (error) {
            console.error('Error fetching occupancy report:', error);
            setOccupancyError('Failed to load occupancy report. Please try again.');
        } finally {
            setIsLoadingOccupancy(false);
        }
    };

    const handleOccupancyFilterChange = (field, value) => {
        setOccupancyFilters(prev => ({ ...prev, [field]: value }));
        setOccupancyPage(0);
    };

    const resetOccupancyFilters = () => {
        setOccupancyFilters({
            startDate: '',
            endDate: '',
            period: 'daily',
            roomTypeId: ''
        });
        setOccupancyPage(0);
    };

    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    const handleExport = async (format) => {
        handleExportClose();
        setIsExporting(true);
        setOccupancyError(null);
        try {
            // Force the API to return CSV, which we will convert in the browser
            const params = { ...occupancyFilters, format: 'csv' };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) {
                    delete params[key];
                }
            });
            const res = await ReportService.exportOccupancyReport(tenantId, params);
            
            // Extract the raw CSV text safely
            let csvText = '';
            if (res.data instanceof Blob) {
                csvText = await res.data.text();
            } else if (typeof res.data === 'string') {
                csvText = res.data;
            } else {
                csvText = JSON.stringify(res.data);
            }
            
            // Parse CSV into an array of arrays
            const fileNameBase = `Occupancy_Report_${new Date().toISOString().split('T')[0]}`;
            await convertCsvToExportFormat(csvText, format, fileNameBase);
        } catch (error) {
            console.error(`Error exporting occupancy report as ${format}:`, error);
            setOccupancyError(`Failed to export ${format.toUpperCase()} report. Please try again.`);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box>
            <Snackbar 
                open={!!occupancyError} 
                autoHideDuration={6000} 
                onClose={() => setOccupancyError(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="warning" onClose={() => setOccupancyError(null)}>{occupancyError}</Alert>
            </Snackbar>

            <Paper sx={{ p: 2, mb: 3, borderRadius: '12px', border: '1px solid #edf1f6', bgcolor: '#ffffff', boxShadow: 'none' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <TextField
                            label="Start Date"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={occupancyFilters.startDate}
                            onChange={(e) => handleOccupancyFilterChange('startDate', e.target.value)}
                            sx={standardFilterInputSx}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            label="End Date"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={occupancyFilters.endDate}
                            onChange={(e) => handleOccupancyFilterChange('endDate', e.target.value)}
                            sx={standardFilterInputSx}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={occupancyFilters.period}
                                displayEmpty
                                onChange={(e) => handleOccupancyFilterChange('period', e.target.value)}
                                sx={standardFilterSelectSx}
                            >
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">Monthly</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={occupancyFilters.roomTypeId}
                                displayEmpty
                                onChange={(e) => handleOccupancyFilterChange('roomTypeId', e.target.value)}
                                sx={standardFilterSelectSx}
                            >
                                <MenuItem value="">All Room Types</MenuItem>
                                {roomTypes.map((type) => (
                                    <MenuItem key={type.id || type._id} value={type.id || type._id}>
                                        {type.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    {(occupancyFilters.startDate !== '' || occupancyFilters.endDate !== '' || occupancyFilters.period !== 'daily' || occupancyFilters.roomTypeId !== '') && (
                        <Button variant="outlined" startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} sx={standardResetButtonSx} onClick={resetOccupancyFilters} disabled={isExporting}>Reset</Button>
                    )}
                    <Button 
                        variant="outlined" 
                        onClick={handleExportClick}
                        disabled={isExporting}
                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={standardSecondaryButtonSx}
                    >
                        {isExporting ? 'Exporting...' : 'Export Report'}
                    </Button>
                    <Menu
                        anchorEl={exportAnchorEl}
                        open={Boolean(exportAnchorEl)}
                        onClose={handleExportClose}
                    >
                        <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
                        <MenuItem onClick={() => handleExport('excel')}>Export as Excel</MenuItem>
                        <MenuItem onClick={() => handleExport('pdf')}>Export as PDF</MenuItem>
                    </Menu>
                </Box>
            </Paper>

            {occupancySummary && !isLoadingOccupancy && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">{occupancySummary.occupancyPercent || '0.00'}%</Typography>
                            <Typography variant="body2" color="text.secondary">Occupancy</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">{occupancySummary.roomsOccupied || 0} / {occupancySummary.totalRooms || 0}</Typography>
                            <Typography variant="body2" color="text.secondary">Rooms Occupied</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">
                                {formatCurrency(occupancySummary.adr || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">ADR</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">
                                {formatCurrency(occupancySummary.revPar || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">RevPAR</Typography>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Occupancy Rate</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoadingOccupancy ? (
                            Array.from(new Array(5)).map((_, idx) => (
                                <TableRow key={idx}>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                </TableRow>
                            ))
                        ) : (
                            occupancyData.length > 0 ? (
                                occupancyData.map((row, idx) => (
                                    <TableRow key={idx} hover>
                                        <TableCell>{row.date || '-'}</TableCell>
                                        <TableCell>
                                            <Chip label={`${row.occupancyPercent !== undefined ? Number(row.occupancyPercent).toFixed(2) : 0}%`} size="small" color={(row.occupancyPercent || 0) > 80 ? "success" : "default"} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                                        <Typography color="text.secondary">No occupancy records found for the selected period.</Typography>
                                    </TableCell>
                                </TableRow>
                            )
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={occupancyData.length >= occupancyRowsPerPage ? -1 : occupancyData.length}
                    page={occupancyPage}
                    onPageChange={(e, newPage) => setOccupancyPage(newPage)}
                    rowsPerPage={occupancyRowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setOccupancyRowsPerPage(parseInt(e.target.value, 10));
                        setOccupancyPage(0);
                    }}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>
        </Box>
    );
}
